
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import TrainingInquiryModal from './components/TrainingInquiryModal';

export default function Services() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState('');

  useEffect(() => {
    // SEO Meta tags for Services page
    document.title = 'トレーニング | 株式会社OnYourSide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '企業研修から個人向け講座まで、幅広いニーズにお応えする質の高いプログラムをご提供。リーダーシップ研修、コンプライアンス研修、アサーティブコミュニケーション研修など。');
    }

    // Schema.org JSON-LD for Services page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "トレーニング",
      "description": "企業研修から個人向け講座まで、幅広いニーズにお応えする質の高いプログラムをご提供しています。",
      "provider": {
        "@type": "Organization",
        "name": "株式会社OnYourSide"
      },
      "serviceType": "Training Programs",
      "areaServed": "Japan",
      "url": `${import.meta.env.VITE_SITE_URL}/services`
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleProgramInquiry = (programTitle: string) => {
    setSelectedProgram(programTitle);
    setIsModalOpen(true);
  };

  const corporatePrograms = [
    {
      title: 'リーダーシップ研修',
      duration: '2日間',
      participants: '10-20名',
      description: '管理職向けのリーダーシップスキル向上プログラム。チームマネジメントと人材育成の実践的手法を学びます。',
      features: ['360度評価', 'ケーススタディ', 'アクションプラン作成', 'フォローアップセッション']
    },
    {
      title: 'コンプライアンス研修',
      duration: '3.5時間～',
      participants: '10-30名',
      description: '企業の法令遵守と倫理的行動を促進する研修プログラム。リスク管理と適切な判断力を身につけます。',
      features: ['法令知識習得', 'ケーススタディ分析', 'リスクアセスメント', '実践的演習']
    },
    {
      title: 'アサーティブコミュニケーション研修',
      duration: '3.5時間～',
      participants: '8-20名',
      description: '自分も相手も大切にするコミュニケーション技術を習得。建設的な意見交換と関係構築を実現します。',
      features: ['自己表現技法', '傾聴スキル', 'WIN-WINの関係構築', 'ロールプレイング']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            トレーニング
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            企業向け研修プログラムで、組織の成長と人材育成をサポートいたします
          </p>
        </div>
      </section>

      {/* Service Navigation */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              サービス一覧
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="bg-white rounded-lg p-6 shadow-md border-2 border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">トレーニング</h3>
                <p className="text-gray-600 mb-4">企業向け研修プログラムを提供</p>
                <span className="text-blue-600 font-medium">現在のページ</span>
              </div>
              <Link to="/hr-consulting" className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow border-2 border-transparent hover:border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-2">人事コンサルティング</h3>
                <p className="text-gray-600 mb-4">人事制度設計・採用支援・組織開発・ダイバーシティ推進</p>
                <span className="text-blue-600 font-medium">詳細を見る →</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              企業向け研修プログラム
            </h2>
            <p className="text-xl text-gray-600">
              組織の課題に合わせてカスタマイズ可能な研修プログラム
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {corporatePrograms.map((program, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-30000 transition-colors">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {program.title}
                </h3>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-600">期間: {program.duration}</span>
                  <span className="text-sm text-gray-600">定員: {program.participants}</span>
                </div>
                <div className="text-lg font-medium text-blue-600 mb-4">
                  料金はお問い合わせください
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {program.description}
                </p>
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">プログラム内容</h4>
                  <ul className="space-y-2">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-blue-600 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <Button 
                  className="w-full whitespace-nowrap"
                  onClick={() => handleProgramInquiry(program.title)}
                >
                  詳細・お申し込み
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Training */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                カスタム研修
              </h2>
              <p className="text-xl text-gray-600">
                お客様のご要望に合わせて完全オーダーメイドの研修を企画・実施いたします
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  こんな課題をお持ちの企業様へ
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-blue-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">既存の研修プログラムでは課題が解決できない</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-blue-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">業界特有の課題に対応した研修が必要</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-blue-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">短期間で効果的な成果を出したい</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-blue-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">継続的なフォローアップが欲しい</span>
                  </li>
                </ul>
              </div>
              
              <div className="text-center">
                <img
                  src="https://public.readdy.ai/ai/img_res/4b0aa8c4417bd5ab6901427f8d5cee5d.jpg"
                  alt="カスタム研修相談"
                  className="rounded-xl shadow-lg object-cover w-full h-80 mb-6"
                />
                <Button size="lg" className="whitespace-nowrap">
                  カスタム研修について相談する
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Public Sector Services Section */}
      <section className="py-20 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              公務員向け特化サービス
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              公務員の皆様に特化した研修プログラムもご提供しています
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  公務員向け専門研修
                </h3>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">公務員倫理・コンプライアンス研修</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">住民対応力向上プログラム</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">行政サービス向上研修</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">公務員向けリーダーシップ研修</span>
                  </li>
                </ul>
                <a 
                  href="https://public.oyside.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 whitespace-nowrap">
                    公務員向けサイトを見る
                  </Button>
                </a>
              </div>
              
              <div className="text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20government%20office%20training%20session%20with%20diverse%20Japanese%20public%20servants%20attending%20workshop%2C%20female%20trainer%20presenting%20to%20civil%20servants%2C%20modern%20government%20building%20conference%20room%2C%20collaborative%20learning%20atmosphere%2C%20inspiring%20public%20service%20education%20environment%20in%20Japan&width=500&height=400&seq=publicj2&orientation=landscape"
                  alt="公務員向け研修"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            トレーニングプログラムのお申し込み
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            ご質問やお見積もりのご依頼など、お気軽にお問い合わせください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 whitespace-nowrap">
                お問い合わせ
              </Button>
            </Link>
            <Link to="/cases">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
                研修事例を見る
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              受講者の声
            </h2>
            <p className="text-xl text-gray-600">
              実際にトレーニングを受けられた企業様からのお声をご紹介します
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businesswoman%20in%20her%2040s%20with%20black%20hair%2C%20confident%20smile%2C%20wearing%20business%20suit%2C%20office%20background%2C%20warm%20and%20approachable%20expression%2C%20Asian%20features%2C%20corporate%20headshot%20style%20portrait&width=80&height=80&seq=testimonial1j&orientation=squarish"
                  alt="T.K様"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">T.K様</h4>
                  <p className="text-sm text-gray-600">A商事株式会社</p>
                  <p className="text-sm text-gray-500">人事部長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "チーム力向上研修のおかげで、部署全体のコミュニケーションが格段に改善されました。実践的な内容で即座に職場で活用できています。"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20woman%20in%20her%2030s%20with%20black%20hair%2C%20friendly%20smile%2C%20business%20casual%20attire%2C%20modern%20office%20environment%2C%20confident%20and%20positive%20expression%2C%20Asian%20facial%20features&width=80&height=80&seq=testimonial2j&orientation=squarish"
                  alt="S.M様"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">S.M様</h4>
                  <p className="text-sm text-gray-600">D株式会社</p>
                  <p className="text-sm text-gray-500">マーケティング部長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "リーダーシップ研修を受講して、チームマネジメントに自信を持てるようになりました。講師の丁寧な指導に感謝しています。"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20middle-aged%20businessman%20with%20black%20hair%2C%20reliable%20expression%2C%20business%20shirt%20and%20tie%2C%20industrial%20office%20background%2C%20leadership%20qualities%20visible%2C%20Asian%20features&width=80&height=80&seq=testimonial3j&orientation=squarish"
                  alt="Y.H様"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">Y.H様</h4>
                  <p className="text-sm text-gray-600">X製造業</p>
                  <p className="text-sm text-gray-500">工場長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "リーダーシップ研修により、部下との関係が改善され、チーム全体の生産性が向上しました。"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              標準価格表
            </h2>
          </div>

          <div className="bg-gray-50 rounded-xl p-12">
            <div className="text-center">
              <div className="bg-white rounded-lg p-8 shadow-md inline-block">
                <div className="text-3xl font-bold text-blue-600">
                  ～3時間　20万円（税別）～
                </div>
              </div>

              <div className="mt-12">
                <p className="text-gray-600 mb-6">
                  詳細な料金については、お気軽にお問い合わせください。
                </p>
                <Link to="/contact">
                  <Button size="lg" className="whitespace-nowrap">
                    お問い合わせ
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TrainingInquiryModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        programTitle={selectedProgram}
      />

      <Footer />
    </div>
  );
}
